import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, ChevronUp, Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";

import { getPosClient } from "../../lib/api";

interface MfaManagerProps {
  readonly enrolled: boolean;
  readonly required?: boolean;
}

export function MfaManager({ enrolled, required = false }: MfaManagerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [otpUri, setOtpUri] = useState<string | null>(null);
  const [secretCode, setSecretCode] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [setupDone, setSetupDone] = useState(false);

  const setup = useMutation({
    mutationFn: () => getPosClient().mfaSetup(),
    onSuccess: (data) => {
      setOtpUri(data.otp_uri);
      setSecretCode(data.secret_code);
      setCode("");
      setCodeError(null);
    },
  });

  const verify = useMutation({
    mutationFn: (userCode: string) => getPosClient().mfaVerify({ user_code: userCode }),
    onSuccess: () => {
      setSetupDone(true);
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: () => {
      setCodeError(t("profile.mfa.invalidCode"));
    },
  });

  function handleSetupClick() {
    if (!expanded) {
      setExpanded(true);
      if (!otpUri) setup.mutate();
    } else {
      setExpanded(false);
    }
  }

  function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setCodeError(null);
    if (!/^\d{6}$/.test(code)) {
      setCodeError(t("profile.mfa.codeFormat"));
      return;
    }
    verify.mutate(code);
  }

  const buttonLabel = enrolled
    ? t("profile.mfa.reAddAuthenticator")
    : t("profile.mfa.setupAuthenticator");

  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground">
          {t("profile.mfa.statusLabel")}{" "}
          <span
            className={
              enrolled
                ? "inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-400"
                : "font-semibold text-strong"
            }
          >
            {enrolled && <ShieldCheck className="h-3.5 w-3.5" aria-hidden />}
            {!enrolled && <ShieldOff className="h-3.5 w-3.5 inline" aria-hidden />}{" "}
            {enrolled ? t("profile.mfa.enrolled") : t("profile.mfa.notEnrolled")}
            {required && (
              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                {t("profile.mfa.required")}
              </span>
            )}
          </span>
        </p>
        <button
          type="button"
          onClick={handleSetupClick}
          disabled={setup.isPending}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-3 py-1.5 text-xs font-medium text-foreground hover:bg-surface-muted disabled:opacity-60"
        >
          {setup.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
          {buttonLabel}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      </div>

      {expanded && (
        <div className="rounded-lg border border-line bg-surface-muted p-4 space-y-4">
          {setupDone ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
              <Check className="h-4 w-4" aria-hidden />
              {t("profile.mfa.setupSuccess")}
            </div>
          ) : otpUri ? (
            <>
              <p className="text-sm text-foreground">{t("profile.mfa.scanInstruction")}</p>
              <div className="flex justify-center rounded-md bg-white p-3">
                <QRCodeSVG value={otpUri} size={160} />
              </div>
              {secretCode && (
                <p className="text-center text-xs text-muted">
                  {t("profile.mfa.manualEntry")}{" "}
                  <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-xs text-strong select-all">
                    {secretCode}
                  </code>
                </p>
              )}
              <form onSubmit={handleVerify} className="space-y-2">
                <label className="block">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    {t("profile.mfa.codeLabel")}
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                      setCodeError(null);
                    }}
                    placeholder="000000"
                    className="mt-1 block w-full rounded-md bg-surface px-3 py-2 text-center font-mono text-lg tracking-widest text-strong ring-1 ring-inset ring-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                  />
                  {codeError && (
                    <span className="mt-1 block text-xs text-red-600 dark:text-red-400">
                      {codeError}
                    </span>
                  )}
                </label>
                <button
                  type="submit"
                  disabled={code.length !== 6 || verify.isPending}
                  className="w-full rounded-md bg-primary py-2 text-sm font-semibold text-primary-foreground shadow-elev-1 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {verify.isPending ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {t("profile.mfa.verifying")}
                    </span>
                  ) : (
                    t("profile.mfa.confirm")
                  )}
                </button>
              </form>
            </>
          ) : setup.isError ? (
            <p className="text-sm text-red-600 dark:text-red-400">{t("profile.mfa.setupError")}</p>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t("profile.mfa.loadingSetup")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
